import { IZendeskTicket } from "@kuika/kuika-cl-model/src/models/zendesk";
import { ReactNode } from "react";
import DateService from "../../../services/date-service";
import { IWithSDProps, withSD } from "../../../shared/hoc/screen-design-consumer";
import { PureSdComponent } from "../../../shared/pure-sd";
import KuikaBadge from "../../ui-kit/kuika-badge/kuika-badge";
import KuikaStatusGlyph, { KuikaStatusGlyphVariant } from "../../ui-kit/kuika-status-glyph/kuika-status-glyph";
import KuikaSvg from "../../ui-kit/kuika-svg/kuika-svg";
import { HelpdeskBoardSkeleton } from "./helpdesk-skeleton";
import "./helpdesk-status-board.scss";
import { getTicketIdColor } from "./ticket-id-color";
import { stripMarkdownPreview } from "./ticket-preview-text";
import { ZendeskTicketStatus } from "./zendesk-status-badge";
import {
  getRequesterName,
  getRequesterOptions,
  groupTicketsByStatus,
  IZendeskRequesterOption,
  IZendeskTicketGroups,
  ticketMatchesSearch
} from "./zendesk-ticket-filter";

interface Props {
  searchText?: string;
  /** Mirrors the ticket panel's CreateNewTicket gate; false hides the create affordance. */
  canCreateTicket: boolean;
  /** Opens the composer. The board itself has no room to write in, so it hands over to Inbox. */
  onCreateTicket: () => void;
  /** Opens one ticket. Same reason: reading a thread belongs to the Inbox view. */
  onSelectTicket: (ticketId: number) => void;
}

interface BoardColumn {
  status: ZendeskTicketStatus;
  label: string;
  tickets: IZendeskTicket[];
}

/**
 * The Helpdesk's own vocabulary mapped onto the shared glyph's three shapes: an open ticket is
 * waiting on us (not started), pending is mid-conversation, solved is closed out. The colours
 * stay the ones the board already used, so only the SHAPE is new.
 */
const STATUS_GLYPH_VARIANT: Record<ZendeskTicketStatus, KuikaStatusGlyphVariant> = {
  open: "not-started",
  pending: "in-progress",
  solved: "done"
};

const STATUS_GLYPH_COLOR: Record<ZendeskTicketStatus, string> = {
  open: "var(--blue-5)",
  pending: "var(--yellow-5)",
  // Same traffic light as the chips (see `zendesk-status-badge.tsx`): the column head has to
  // speak the language of what is inside it.
  solved: "var(--green-5)"
};

/**
 * C · Status board — status stops being a badge and becomes the layout, so the whole support
 * load reads at a glance. The Solved group that sits collapsed in the list gets a column of
 * its own here.
 */
class HelpdeskStatusBoard extends PureSdComponent<Props & IWithSDProps, Record<string, never>> {
  private renderCard = (ticket: IZendeskTicket, requesterName?: string): ReactNode => (
    <button
      key={ticket.id}
      data-testid="helpdeskBoardCard"
      className="kuika-helpdesk-board__card"
      onClick={() => this.props.onSelectTicket(ticket.id)}
    >
      <span className="kuika-helpdesk-board__card-title-row">
        <span className="kuika-helpdesk-board__card-title">{ticket.subject}</span>
        <KuikaBadge content={`#${ticket.id}`} color={getTicketIdColor(ticket.id)} variant="subtle" />
      </span>
      <span className="kuika-helpdesk-board__card-description">{stripMarkdownPreview(ticket.description)}</span>
      <span className="kuika-helpdesk-board__card-footer">
        {requesterName && <span className="kuika-helpdesk-board__card-requester">{requesterName}</span>}
        <span className="kuika-helpdesk-board__card-date">{DateService.getHumanizedDate(ticket.updatedAt)}</span>
      </span>
    </button>
  );

  private renderColumn = (
    column: BoardColumn,
    requesterNameOf: (ticket: IZendeskTicket) => string | undefined
  ): ReactNode => (
    <section
      key={column.status}
      data-testid={`helpdeskBoardColumn-${column.status}`}
      className={`kuika-helpdesk-board__column kuika-helpdesk-board__column--${column.status}`}
    >
      <header className="kuika-helpdesk-board__column-header">
        {/*
          Shape, not colour. The column LABEL is tinted from the same three-value scale, so the
          8px dot that used to sit here left the header with no second channel at all.
        */}
        <KuikaStatusGlyph
          variant={STATUS_GLYPH_VARIANT[column.status]}
          color={STATUS_GLYPH_COLOR[column.status]}
          label={column.label}
          size={13}
          className="kuika-helpdesk-board__glyph"
        />
        <span className="kuika-helpdesk-board__column-label">{column.label}</span>
        <KuikaBadge content={String(column.tickets.length)} color="grey" />
      </header>
      <div className="kuika-helpdesk-board__column-body">
        {column.tickets.map((ticket: IZendeskTicket) => this.renderCard(ticket, requesterNameOf(ticket)))}
        {column.status === "open" && this.props.canCreateTicket && (
          <button className="kuika-helpdesk-board__new-ticket" onClick={this.props.onCreateTicket}>
            <KuikaSvg name="plus" width={14} height={14} />
            New ticket
          </button>
        )}
        {column.tickets.length === 0 && column.status !== "open" && (
          <p className="kuika-helpdesk-board__empty">Nothing {column.label.toLowerCase()}.</p>
        )}
      </div>
    </section>
  );

  render() {
    // The board had no loading affordance at all: three empty columns, an unexplained "New
    // ticket" button under Open, and "Nothing pending." under a list that was still loading.
    if (this.props.sd.isTicketsLoading && this.props.sd.zendeskTicketList.length === 0) {
      return <HelpdeskBoardSkeleton />;
    }

    const requesterNames: Record<number, string> = this.props.sd.zendeskRequesterNames;
    const requesterOptions: IZendeskRequesterOption[] = getRequesterOptions(
      this.props.sd.zendeskTicketList,
      requesterNames
    );
    // Same rule as the list: a creator name is noise until tickets come from more than one person.
    const hasMultipleCreators: boolean = requesterOptions.length > 1;
    const requesterNameOf = (ticket: IZendeskTicket): string | undefined =>
      hasMultipleCreators ? getRequesterName(ticket, requesterNames) : undefined;

    const groups: IZendeskTicketGroups = groupTicketsByStatus(
      this.props.sd.zendeskTicketList,
      (ticket: IZendeskTicket) => ticketMatchesSearch(ticket, this.props.searchText)
    );

    const columns: BoardColumn[] = [
      { status: "open", label: "Open", tickets: groups.open },
      { status: "pending", label: "Pending", tickets: groups.pending },
      { status: "solved", label: "Solved", tickets: groups.solved }
    ];

    return (
      <div data-testid="helpdeskBoard" className="kuika-helpdesk-board">
        {columns.map((column: BoardColumn) => this.renderColumn(column, requesterNameOf))}
      </div>
    );
  }

  /** Renders on any change that reaches it — see ZendeskCanvas. */
  checkNeedRender = (): boolean => {
    return true;
  };
}

const helpdeskStatusBoard = withSD<Props>(HelpdeskStatusBoard);
export { helpdeskStatusBoard as HelpdeskStatusBoard };
